// src/pages/ContactPage.jsx
import { useState } from "react";
import { FaDiscord, FaTwitch, FaInstagram, FaTiktok, FaEnvelope, FaMapMarkerAlt, FaCheckCircle } from "react-icons/fa";

function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    _gotcha: "", // honeypot — bots fill this, humans never see it
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Honeypot check — if filled, silently succeed (it's a bot)
    if (formData._gotcha) {
      setSubmitted(true);
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch("https://formspree.io/f/mwvnzykn", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: "", email: "", subject: "", message: "", _gotcha: "" });
      }
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const socialLinks = [
    { name: "Discord", icon: FaDiscord, url: "https://discord.gg/SGXXCMpCxf", color: "hover:text-indigo-400" },
    { name: "Twitch", icon: FaTwitch, url: "https://www.twitch.tv/primevaloresports", color: "hover:text-purple-400" },
    { name: "TikTok", icon: FaTiktok, url: "https://www.tiktok.com/@primevaloresports", color: "hover:text-pink-400" },
    { name: "Instagram", icon: FaInstagram, url: "https://www.instagram.com/primevaloresports", color: "hover:text-pink-500" },
  ];

  return (
    <main className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 to-black"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-6">
            Get in <span className="text-red-600">Touch</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Have questions? Want to partner with us? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">

            {/* Contact Form */}
            <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                  <FaCheckCircle className="text-green-500 text-6xl mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-3">Message Sent!</h3>
                  <p className="text-gray-400 mb-8">
                    Thanks for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide rounded-lg transition-colors"
                  >
                    Send Another
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-white mb-6 uppercase">
                    Send us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot — hidden from humans, bots fill it */}
                    <input
                      type="text"
                      name="_gotcha"
                      value={formData._gotcha}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                      style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-400 mb-2">
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-600 transition-colors"
                      >
                        <option value="">Select a topic</option>
                        <option value="general">General Inquiry</option>
                        <option value="recruitment">Team Recruitment</option>
                        <option value="sponsorship">Sponsorship</option>
                        <option value="media">Media & Press</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors resize-none"
                        placeholder="Tell us what's on your mind..."
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full px-8 py-4 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wide rounded-lg transition-colors"
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </button>
                  </form>
                </>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8">

              {/* Email & Location */}
              <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
                <h2 className="text-2xl font-bold text-white mb-6 uppercase">
                  Contact Info
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <FaEnvelope className="text-red-600 text-xl flex-shrink-0" />
                    <div>
                      <p className="text-gray-400 text-sm">Email Us</p>
                      <a
                        href="mailto:primevalorgaming@gmail.com"
                        className="text-white hover:text-red-500 transition-colors"
                      >
                        primevalorgaming@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <FaMapMarkerAlt className="text-red-600 text-xl flex-shrink-0" />
                    <div>
                      <p className="text-gray-400 text-sm">Based In</p>
                      <p className="text-white">Caribbean Region</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
                <h2 className="text-2xl font-bold text-white mb-6 uppercase">
                  Connect With Us
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-3 p-4 bg-gray-800 rounded-lg text-gray-300 ${social.color} transition-colors group`}
                    >
                      <social.icon className="text-2xl" />
                      <span className="font-medium">{social.name}</span>
                    </a>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Join Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-red-900/20 to-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Want to Join Prime Valor?
          </h2>
          <p className="text-gray-400 mb-8">
            We're always looking for talented players and content creators to join our team.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/join"
              className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide rounded transition-colors"
            >
              Apply Now
            </a>
            <a
              href="https://discord.gg/SGXXCMpCxf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white font-bold uppercase tracking-wide rounded transition-colors"
            >
              <FaDiscord className="text-xl" />
              Join Discord
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default ContactPage;
